import React, { useState } from "react";
import { Link } from "react-router-dom";
import { User, Users, Users2, Layers, Mail, ArrowUpRight, Sparkles, Send } from "lucide-react";
import { ARTIST } from "../mock";

const FORMULES = [
  {
    key: "solo",
    label: "Solo",
    subtitle: "Guitare · Piano · Voix · Bande son",
    description:
      "Un format intimiste et chaleureux, idéal pour les espaces réduits, cocktails et moments acoustiques.",
    Icon: User,
    accent: "text-red-500",
    border: "hover:border-red-500/50",
  },
  {
    key: "duo",
    label: "Duo",
    subtitle: "À deux musiciens · Guitare · Piano · Voix · Bande son",
    description:
      "La proximité du solo avec une couleur musicale enrichie. Parfait pour vins d'honneur et événements feutrés.",
    Icon: Users,
    accent: "text-blue-600",
    border: "hover:border-blue-600/50",
  },
  {
    key: "groupe",
    label: "Groupe",
    subtitle: "Formation complète",
    description:
      "L'énergie scène en grand format : batterie, basse, claviers, guitares. Pour faire vibrer la piste de danse.",
    Icon: Users2,
    accent: "text-red-500",
    border: "hover:border-red-500/50",
  },
  {
    key: "plateau",
    label: "Plateau multi-artistes",
    subtitle: "Soirée scénique sur mesure",
    description:
      "Une programmation construite avec d'autres artistes pour offrir une véritable soirée concert variée.",
    Icon: Layers,
    accent: "text-blue-600",
    border: "hover:border-blue-600/50",
  },
];

export default function PrestationsEvenements() {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    date: "",
    typeEvent: "",
    lieu: "",
    formule: "",
    message: "",
  });
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim()) {
      setError("Merci d'indiquer au moins votre nom et votre email.");
      return;
    }
    setError("");
    const subject = `Demande de devis — ${form.typeEvent || "Événement musical"}${
      form.formule ? ` (${form.formule})` : ""
    }`;
    const body = [
      `Bonjour,`,
      ``,
      `Je souhaite obtenir un devis pour un événement musical.`,
      ``,
      `— Nom : ${form.nom}`,
      `— Email : ${form.email}`,
      form.telephone ? `— Téléphone : ${form.telephone}` : null,
      form.date ? `— Date : ${form.date}` : null,
      form.typeEvent ? `— Type d'événement : ${form.typeEvent}` : null,
      form.lieu ? `— Lieu : ${form.lieu}` : null,
      form.formule ? `— Formule souhaitée : ${form.formule}` : null,
      ``,
      `— Message :`,
      form.message || "(aucun)",
      ``,
      `Cordialement,`,
      form.nom,
    ]
      .filter(Boolean)
      .join("%0D%0A");

    window.location.href = `mailto:${ARTIST.email}?subject=${encodeURIComponent(
      subject
    )}&body=${body}`;
  };

  return (
    <div
      className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16"
      data-testid="prestations-evenements-page"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10 flex-wrap">
        <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">
          Accueil
        </Link>
        <span>/</span>
        <Link to="/prestations" className="hover:text-white" data-testid="breadcrumb-prestations">
          Événementiel
        </Link>
        <span>/</span>
        <span className="text-white">Événements musicaux</span>
      </div>

      {/* Header */}
      <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            06 · Booking
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[8.5vw] leading-[0.85] tracking-tight">
            Événements
            <br />
            <span className="text-blue-600">musicaux</span>
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
              4 formules · Sur devis
            </span>
          </div>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Vince Rodi propose plusieurs formules adaptées à votre événement, de l'intimiste à la grande scène. Choisissez la formule qui correspond à votre projet et obtenons ensemble un devis personnalisé.
          </p>
        </div>
      </div>

      {/* Bandeau tarif global */}
      <div
        className="mb-12 border border-white/15 bg-gradient-to-r from-red-500/5 via-transparent to-blue-600/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
        data-testid="tarif-banner"
      >
        <div className="flex items-center gap-4">
          <Sparkles size={28} strokeWidth={1.4} className="text-red-500 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">
              Tarification
            </div>
            <div className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
              À partir de <span className="text-red-500">200 €</span>
              <span className="text-neutral-500"> — </span>
              <span className="text-blue-600">sur devis</span> selon formule
            </div>
          </div>
        </div>
        <a
          href="#devis-form"
          data-testid="devis-cta"
          className="inline-flex items-center gap-2 border border-white hover:bg-white hover:text-black transition-all px-5 py-3 text-xs uppercase tracking-[0.25em] shrink-0"
        >
          Demander un devis <ArrowUpRight size={14} />
        </a>
      </div>

      {/* Formules */}
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="formules-grid">
        {FORMULES.map((f, i) => (
          <div
            key={f.key}
            data-testid={`formule-card-${f.key}`}
            className={`group relative border border-white/10 ${f.border} bg-neutral-950 p-7 md:p-9 transition-all reveal-up`}
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="flex items-start justify-between mb-7">
              <div className={`${f.accent}`}>
                <f.Icon size={36} strokeWidth={1.3} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                0{i + 1}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              {f.subtitle}
            </div>
            <h2 className="font-display uppercase text-4xl md:text-5xl leading-none tracking-tight mb-5">
              {f.label}
            </h2>
            <p className="text-sm text-neutral-300 leading-relaxed">{f.description}</p>
          </div>
        ))}
      </div>

      {/* Note finale */}
      <div className="mt-14 border-t border-white/10 pt-10 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-3">
            Sur mesure
          </div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Chaque événement est unique.
          </h3>
          <p className="mt-4 text-neutral-400 text-sm max-w-xl leading-relaxed">
            Mariage, soirée d'entreprise, festival, fête de village : indiquez-nous votre projet et nous construirons ensemble la formule idéale. Réponse sous 48h.
          </p>
        </div>
        <div className="md:col-span-5 flex md:justify-end">
          <a
            href={`mailto:${ARTIST.email}?subject=Demande%20de%20devis%20—%20Événement%20musical`}
            className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black transition-all px-6 py-4 text-xs uppercase tracking-[0.25em]"
            data-testid="contact-mail-cta"
          >
            <Mail size={16} /> {ARTIST.email}
          </a>
        </div>
      </div>
      {/* FORMULAIRE DE DEVIS */}
      <div
        className="mt-16 border border-white/15 bg-neutral-950 p-6 md:p-10"
        id="devis-form"
        data-testid="devis-form-section"
      >
        <div className="grid md:grid-cols-12 gap-8 mb-8">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 mb-3">
              Formulaire
            </div>
            <h3 className="font-display uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">
              Demande<br />
              <span className="text-blue-600">de devis</span>
            </h3>
            <p className="mt-5 text-sm text-neutral-400 leading-relaxed max-w-sm">
              Remplissez ce formulaire pour décrire votre projet. Un email pré-rempli s'ouvrira pour finaliser l'envoi. Réponse sous 48h.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4"
            data-testid="devis-form"
          >
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Nom · Prénom *
              </label>
              <input
                type="text"
                required
                value={form.nom}
                onChange={update("nom")}
                data-testid="form-nom"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                data-testid="form-email"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Téléphone
              </label>
              <input
                type="tel"
                value={form.telephone}
                onChange={update("telephone")}
                data-testid="form-telephone"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Date de l'événement
              </label>
              <input
                type="date"
                value={form.date}
                onChange={update("date")}
                data-testid="form-date"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors text-neutral-300"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Type d'événement
              </label>
              <select
                value={form.typeEvent}
                onChange={update("typeEvent")}
                data-testid="form-type"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors"
              >
                <option value="">— Sélectionner —</option>
                <option>Mariage</option>
                <option>Entreprise</option>
                <option>Festival / Concert</option>
                <option>Bar / Restaurant</option>
                <option>Municipal / Fête de village</option>
                <option>Autre</option>
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Formule souhaitée
              </label>
              <select
                value={form.formule}
                onChange={update("formule")}
                data-testid="form-formule"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors"
              >
                <option value="">— Sélectionner —</option>
                <option>Solo</option>
                <option>Duo</option>
                <option>Groupe</option>
                <option>Plateau multi-artistes</option>
                <option>À discuter</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Lieu (ville · département)
              </label>
              <input
                type="text"
                value={form.lieu}
                onChange={update("lieu")}
                placeholder="Ex. Marseille · 13"
                data-testid="form-lieu"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-neutral-600"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Message libre
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder="Décrivez votre projet, l'ambiance souhaitée, la durée, etc."
                data-testid="form-message"
                className="w-full bg-black border border-white/15 focus:border-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-neutral-600 resize-none"
              />
            </div>

            {error && (
              <div
                className="sm:col-span-2 text-xs text-red-500 border border-red-500/40 bg-red-500/5 px-3 py-2"
                data-testid="form-error"
              >
                {error}
              </div>
            )}

            <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-2">
              <div className="text-[11px] text-neutral-500 leading-snug">
                * Champs obligatoires. En cliquant sur Envoyer, votre app mail s'ouvre avec un message pré-rempli.
              </div>
              <button
                type="submit"
                data-testid="form-submit"
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-red-500 hover:text-white transition-all px-6 py-4 text-xs uppercase tracking-[0.3em] font-medium"
              >
                Envoyer la demande <Send size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
